import { useMemo } from "react";
import axios from "axios";
import apiClient from "src/api/axiosClient";
import { CustomAxiosRequestConfig } from "src/api/axiosClient/types";
import { resolveFileMimeType } from "src/helpers/files";
import { MediaInfo, GetAssetsParams, MediaRequestOptions, ToastRequestOptions } from "../types";

export class FileUploadError extends Error {
  readonly cause?: unknown;

  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'FileUploadError';
    this.cause = cause;
  }
}

const uploadFileToS3 = async (file: File, presignedUrl: string, uploadHeaders?: Record<string, string> | null) => {
  try {
    await axios.put(presignedUrl, file, {
      headers: {
        'Content-Type': resolveFileMimeType(file),
        ...uploadHeaders,
      },
    });
  } catch (error) {
    throw new FileUploadError('Upload to storage failed', error);
  }
};

export function useAssetsApi() {
  return useMemo(() => {
    // GET /files: the single list client. AssetLibraryService and AttachFileUrlModal
    // both go through this — do not add a second one, they will drift.
    const getAssets = (params: GetAssetsParams) => {
      // Convert special filter values to backend-understandable mime type patterns
      let mimeType = params.mime_type;
      if (params.mime_type === 'document') {
        // Documents include PDFs and other document types
        mimeType = 'document';
      } else if (params.mime_type === 'calendar') {
        // Other includes calendar events and any other mime types
        mimeType = 'calendar';
      }

      const apiParams = {
        ...params,
        mime_type: mimeType || undefined,
      };

      return apiClient.get<{ items: MediaInfo[]; count: number }>(`/files`, { params: apiParams });
    };

    // PATCH /files/:fileId returns 202 with no body — callers must not read response.data.
    const updateAsset = (companyId: string, assetId: string, name: string) => {
      return apiClient.patch<void>(`/files/${assetId}`, { name }, { params: { companyId } });
    };

    const deleteAsset = (companyId: string, assetId: string) => {
      return apiClient.delete<void>(`/files/${assetId}`, { params: { companyId } });
    };

    const createMedia = (mimeType: string, companyId: string, name?: string, options?: MediaRequestOptions) => {
      const config: CustomAxiosRequestConfig = {
        params: { companyId },
        skipGenericToast: options?.skipGenericToast,
      };

      return apiClient.post<MediaInfo>(
        '/files',
        { mime_type: mimeType, name, permanent: options?.permanent },
        config,
      );
    };

    const verifyMedia = (mediaId: string, companyId: string, options?: ToastRequestOptions) => {
      const config: CustomAxiosRequestConfig = {
        params: { companyId },
        skipGenericToast: options?.skipGenericToast,
      };

      return apiClient.post<MediaInfo>(`files/${mediaId}/verify`, undefined, config);
    };

    const getFileContent = (url: string) => {
      return apiClient.get<Blob>(`files/content`, { params: { url }, responseType: 'blob' });
    };

    /**
     * Requests a freshly signed, short-lived (10 min TTL) download URL for a file.
     * The presigned URL from list responses must not be cached for download — it expires.
     * operationId: file-get-download-url
     */
    const getFileDownloadUrl = (fileId: string, companyId: string) => {
      return apiClient.get<MediaInfo>(`/files/${fileId}/download-url`, {
        params: { companyId },
      });
    };

    const uploadMedia = async (file: File, companyId: string, options?: MediaRequestOptions): Promise<MediaInfo> => {
      const { data: media } = await createMedia(
        resolveFileMimeType(file),
        companyId,
        options?.name ?? file.name,
        options,
      );
      await uploadFileToS3(file, media.url, media.uploadHeaders);
      const { data: verified } = await verifyMedia(media.id, companyId, {
        skipGenericToast: options?.skipGenericToast,
      });

      return verified;
    };

    const uploadPermanentIcon = async (file: File, companyId: string): Promise<string> => {
      const media = await uploadMedia(file, companyId, { permanent: true });
      return media.url;
    };

    return {
      getAssets,
      updateAsset,
      deleteAsset,
      getFileContent,
      getFileDownloadUrl,
      uploadMedia,
      uploadPermanentIcon,
    };
  }, []);
}
